import { FC, useState, useEffect, useRef } from 'react';
import { Button } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { formatMutationResource } from 'utils/misc';
import {
  selectedSlotAtom,
  closeSlotPanelAtom,
  updateFormationSlotMutationAtom,
  toastAtom,
} from '../atoms';
import styles from './slot-notes-panel.module.scss';
import { activeTcWorkspaceAtom } from 'components/type-curve-workspace/atoms';

export interface SlotSaveResult {
  slotId: number;
  valued: boolean;
  notes: string | null;
}

export interface SlotNotesPanelProps {
  onSaved: (result: SlotSaveResult) => void;
  onCancelled: (
    slotId: number,
    originalValued: boolean,
    originalNotes: string | null
  ) => void;
  typeCurveId: string;
}

export const SlotNotesPanel: FC<SlotNotesPanelProps> = ({
  onSaved,
  onCancelled,
}) => {
  const slot = useAtomValue(selectedSlotAtom);
  const close = useSetAtom(closeSlotPanelAtom);
  const [mutation, runMutation] = useAtom(updateFormationSlotMutationAtom);
  const setToast = useSetAtom(toastAtom);
  const activeTcWorkspace = useAtomValue(activeTcWorkspaceAtom);

  const [notes, setNotes] = useState('');

  // Track the original valued state before the optimistic toggle so Cancel
  // can revert it. The slot arrives with the ALREADY-TOGGLED valued, so
  // the original is the opposite.
  const originalValuedRef = useRef<boolean>(true);
  const originalNotesRef = useRef<string | null>(null);

  useEffect(() => {
    if (slot) {
      setNotes(slot.notes ?? '');
      // The parent toggles valued before setting the slot, so the original
      // is the inverse of what we receive.
      originalValuedRef.current = !slot.valued;
      originalNotesRef.current = slot.notes;
    }
  }, [slot]);

  if (!slot) return null;

  const { loading } = formatMutationResource(mutation);
  const slotLabel = slot.uwi ?? `Slot ${slot.slotNumber}`;

  // The slot already has the toggled valued state — describe the action
  // that was taken on click.
  const wasExcluded = slot.valued === false;

  const handleCancel = () => {
    onCancelled(
      slot.slotId,
      originalValuedRef.current,
      originalNotesRef.current
    );
    close();
  };

  const handleSave = () => {
    if (!activeTcWorkspace) return;
    runMutation({
      url: `/type_curve_workspaces/${activeTcWorkspace.id}/header/formation_slots/${slot.id}`,
      type: 'PATCH',
      data: {
        formation_slot: {
          valued: slot.valued,
          notes: notes || null,
        },
      },
      onSuccess: () => {
        close();
        onSaved({
          slotId: slot.slotId,
          valued: slot.valued,
          notes: notes || null,
        });
        setToast({
          kind: 'success',
          title: wasExcluded
            ? `${slotLabel} excluded`
            : `${slotLabel} re-included`,
        });
      },
      onError: () => {
        setToast({
          kind: 'error',
          title: 'Failed to update slot',
        });
      },
    });
  };

  return (
    <aside
      className={styles.aside}
      role="dialog"
      aria-label={`Slot notes — ${slotLabel}`}
    >
      <div className={styles.header}>
        <div className={styles.headerText}>
          <p className={styles.eyebrow}>Notes</p>
          <h2 className={styles.title}>{slotLabel}</h2>
        </div>
        <Button
          hasIconOnly
          size="md"
          kind="ghost"
          onClick={handleCancel}
          iconDescription="Close"
          tooltipAlignment="center"
          tooltipPosition="bottom"
          renderIcon={(props) => <Close size={20} {...props} />}
        />
      </div>

      <div className={styles.body}>
        <p className={styles.description}>
          {wasExcluded
            ? `State the reason to exclude Slot ${slotLabel} in the Notes and click Save to persist.`
            : `State the reason to re-include Slot ${slotLabel} in the Notes and click Save to persist.`}
        </p>
        <textarea
          className={styles.textarea}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add a note for this slot..."
        />
      </div>

      <div className={styles.footer}>
        <Button
          kind="secondary"
          size="md"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          size="md"
          onClick={handleSave}
          disabled={loading || !notes.trim()}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </aside>
  );
};

SlotNotesPanel.displayName = 'SlotNotesPanel';
